import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FamilyCasesService, FamilyCase } from './family-cases.service';
import { environment } from '../../environments/environment';
import { CameraScannerComponent } from '../camera-scanner/camera-scanner.component';

@Component({
	selector: 'app-family-cases',
	standalone: true,
	imports: [CommonModule, FormsModule, CameraScannerComponent],
	templateUrl: './family-cases.component.html'
})
export class FamilyCasesComponent implements OnInit {
	apiBase = environment.apiBase;
	currentDate: Date = new Date();
	searchTerm: string = '';
	fromDate?: string;
	toDate?: string;
	sortKey: keyof FamilyCase | '' = '';
	sortDir: 'asc' | 'desc' = 'asc';
	page = 1;
	pageSize = 10;
	items: FamilyCase[] = [];
	model: FamilyCase = this.createEmpty();
	file?: File;
	imagePreviewUrl: string | null = null;
	isEditing = false;
	isLoading = false;
	errorMsg = '';
	step = 1;
	scannerAvailable = false;
	showCamera = false;

	constructor(private api: FamilyCasesService) { }

	ngOnInit(): void {
		this.reload();
		// Try initialize scanner if SDK present
		this.initScanner();
	}

	createEmpty(): FamilyCase {
		return {
			headNationalId: '',
			memberNationalId: '',
			registryNumber: '',
			fatherFullName: '',
			subjectFullName: '',
			caseType: '',
			caseDescription: '',
			caseDate: new Date().toISOString().substring(0, 10),
			amountUsd: 0,
			grantsCount: 0,
			imagePath: null
		};
	}

	onFileChange(ev: Event) {
		const input = ev.target as HTMLInputElement;
		const f = input.files && input.files[0];
		this.file = f ?? undefined;
		this.imagePreviewUrl = null;
		if (f) {
			const reader = new FileReader();
			reader.onload = e => this.imagePreviewUrl = String(e.target?.result || '');
			reader.readAsDataURL(f);
		}
	}

	async reload() {
		this.isLoading = true;
		this.errorMsg = '';
		this.api.list().subscribe({
			next: data => {
				this.items = data;
				this.isLoading = false;
			},
			error: () => {
				this.errorMsg = 'تعذر الاتصال بالخادم. تأكد من تشغيل الـ API وتكوين CORS.';
				this.isLoading = false;
			}
		});
	}

	onSubmit() {
		// final submit on step 3 only
		if (this.step < 3) {
			this.next();
			return;
		}
		if (!this.isEditing) {
			this.api.create(this.model).subscribe(async created => {
				if (this.file && created.id) {
					this.api.upload(created.id, this.file).subscribe(() => this.reload());
				} else {
					this.reload();
				}
				this.reset();
			}, () => alert('حدث خطأ أثناء الإضافة'));
		} else {
			const id = this.model.id!;
			this.api.update(id, this.model).subscribe(() => {
				if (this.file) {
					this.api.upload(id, this.file!).subscribe(() => this.reload());
				} else {
					this.reload();
				}
				this.reset();
			}, () => alert('حدث خطأ أثناء التعديل'));
		}
	}

	edit(item: FamilyCase) {
		this.isEditing = true;
		this.model = { ...item, caseDate: (item.caseDate || '').substring(0, 10) };
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	cancel() {
		this.reset();
	}

	reset() {
		this.step = 1;
		this.isEditing = false;
		this.model = this.createEmpty();
		this.file = undefined;
		this.imagePreviewUrl = null;
	}

	view(item: FamilyCase) {
		const payload = JSON.stringify(item, null, 2);
		alert(payload);
	}

	remove(item: FamilyCase) {
		if (!item.id) return;
		if (!confirm('هل أنت متأكد من الحذف؟')) return;
		this.api.delete(item.id).subscribe(() => {
			this.items = this.items.filter(x => x.id !== item.id);
		}, () => alert('تعذر الحذف'));
	}

	next() {
		if (this.step === 1) {
			// simple validations
			if (!this.model.headNationalId || !this.model.memberNationalId || !this.model.registryNumber) {
				alert('يرجى تعبئة الحقول الأساسية في الخطوة الأولى');
				return;
			}
		}
		if (this.step === 2) {
			if (!this.model.fatherFullName || !this.model.subjectFullName || !this.model.caseType || !this.model.caseDescription) {
				alert('يرجى تعبئة بيانات الحالة في الخطوة الثانية');
				return;
			}
		}
		this.step = Math.min(3, this.step + 1);
	}

	prev() {
		this.step = Math.max(1, this.step - 1);
	}

	initScanner() {
		const w = window as any;
		try {
			if (w.Dynamsoft && w.Dynamsoft.DWT) {
				this.scannerAvailable = true;
				// Optional license
				// w.Dynamsoft.DWT.ProductKey = 'YOUR_DWT_LICENSE_KEY';
				w.Dynamsoft.DWT.ResourcesPath = 'https://unpkg.com/dwt/dist/';
			} else {
				this.scannerAvailable = false;
			}
		} catch {
			this.scannerAvailable = false;
		}
	}

	scanFromScanner() {
		const w = window as any;
		if (!(w.Dynamsoft && w.Dynamsoft.DWT)) {
			this.showCamera = true;
			return;
		}
		try {
			w.Dynamsoft.DWT.CreateDWTObjectEx(
				{ WebTwainId: 'fcmsScanner' },
				(twain: any) => {
					twain.AcquireImage({
						IfShowUI: true,
						PixelType: 2,
						Resolution: 200
					}, () => {
						// Export first image as blob
						twain.ConvertToBlob([twain.CurrentImageIndexInBuffer], 'image/jpeg', (blob: Blob) => {
							this.file = new File([blob], 'scan.jpg', { type: 'image/jpeg' });
							const reader = new FileReader();
							reader.onload = e => this.imagePreviewUrl = String(e.target?.result || '');
							reader.readAsDataURL(this.file);
						}, () => alert('تعذر تحويل الصورة.'));
					}, () => alert('تم إلغاء المسح.'));
				},
				(err: any) => {
					console.error(err);
					alert('تعذر تهيئة الماسح.');
				}
			);
		} catch (e) {
			console.error(e);
			alert('تعذر إجراء المسح.');
		}
	}

	onCameraCaptured(blob: Blob) {
		this.file = new File([blob], 'scan.jpg', { type: 'image/jpeg' });
		const reader = new FileReader();
		reader.onload = e => this.imagePreviewUrl = String(e.target?.result || '');
		reader.readAsDataURL(this.file);
		this.showCamera = false;
	}

	onCameraClosed() {
		this.showCamera = false;
	}

	get stepProgress(): number {
		if (this.step <= 1) return 33;
		if (this.step === 2) return 66;
		return 100;
	}

	get filteredItems(): FamilyCase[] {
		const q = (this.searchTerm || '').toLowerCase().trim();
		const start = this.fromDate ? new Date(this.fromDate) : null;
		const end = this.toDate ? new Date(this.toDate) : null;
		return this.items.filter(e => {
			// text filter
			const textOk = !q || (
				e.headNationalId?.toLowerCase().includes(q) ||
				e.memberNationalId?.toLowerCase().includes(q) ||
				e.subjectFullName?.toLowerCase().includes(q) ||
				e.fatherFullName?.toLowerCase().includes(q) ||
				e.registryNumber?.toLowerCase().includes(q) ||
				e.caseType?.toLowerCase().includes(q)
			);
			// date filter
			let dateOk = true;
			if (start || end) {
				const d = e.caseDate ? new Date(e.caseDate) : null;
				if (!d) {
					dateOk = false;
				} else {
					if (start && d < new Date(start.toDateString())) dateOk = false;
					if (end) {
						const endDay = new Date(end.toDateString());
						endDay.setHours(23,59,59,999);
						if (d > endDay) dateOk = false;
					}
				}
			}
			return textOk && dateOk;
		});
	}

	get hasActiveFilters(): boolean {
		return !!(this.searchTerm?.trim() || this.fromDate || this.toDate);
	}

	private compare(a: any, b: any): number {
		if (a == null && b == null) return 0;
		if (a == null) return -1;
		if (b == null) return 1;
		if (typeof a === 'number' && typeof b === 'number') return a - b;
		const as = String(a).toLowerCase();
		const bs = String(b).toLowerCase();
		return as < bs ? -1 : as > bs ? 1 : 0;
	}

	get sortedItems(): FamilyCase[] {
		const items = [...this.filteredItems];
		if (!this.sortKey) return items;
		return items.sort((x, y) => {
			const c = this.compare((x as any)[this.sortKey], (y as any)[this.sortKey]);
			return this.sortDir === 'asc' ? c : -c;
		});
	}

	get displayedItems(): FamilyCase[] {
		const start = (this.page - 1) * this.pageSize;
		return this.sortedItems.slice(start, start + this.pageSize);
	}

	get totalPages(): number {
		return Math.max(1, Math.ceil(this.sortedItems.length / this.pageSize));
	}

	setSort(key: keyof FamilyCase) {
		if (this.sortKey === key) {
			this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortKey = key;
			this.sortDir = 'asc';
		}
		this.page = 1;
	}

	onPageSizeChange() { this.page = 1; }
	prevPage() { this.page = Math.max(1, this.page - 1); }
	nextPage() { this.page = Math.min(this.totalPages, this.page + 1); }

	caseTypeBadgeClass(caseType: string | undefined): string {
		switch (caseType) {
			case 'بيانات غير مطابقة للواقع': return 'badge rounded-pill text-bg-warning';
			case 'متوفي': return 'badge rounded-pill text-bg-danger';
			case 'فرد ليس من العائلة': return 'badge rounded-pill text-bg-secondary';
			case 'متزوجة ومضافة': return 'badge rounded-pill text-bg-info';
			case 'الادخل': return 'badge rounded-pill text-bg-dark';
			default: return 'badge rounded-pill text-bg-light text-dark';
		}
	}

	goTo(step: number) {
		// Allow free navigation before submission
		if (step < 1 || step > 3) return;
		this.step = step;
	}
}


